import React, { useState, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import ReactCrop from 'react-image-crop';
import { isEmpty } from 'lodash';
import ReactPlayer from 'react-player';
import 'react-image-crop/dist/ReactCrop.css';
import Loader from '../../../Components/Utility/Loader/Loader';

const cropped = (cropSize) => {
  return cropSize === '4/3' ? 4 / 3 : 1;
};

function DamImageDropzone({ cropSize, acceptFileTypes, editModeUrls, imageUrl, title, onSubmit, onClose, fileIndex, isVideoType, isImageType, translate }) {
  const [image, setImage] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const previewCanvasRef = useRef(null);
  const [validatedSelectedFile, setValidatedSelectedFile] = useState({});
  const imgRef = useRef(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [cropperImageLoader, setImageLoader] = useState(false);
  const [videoThumbnailloader, setVideoThumbnailLoader] = useState(false);

  const [crop, setCrop] = useState({
    unit: '%',
    width: cropSize === 'FREE' && 30,
    height: cropSize === 'FREE' && 30,
    aspect: cropSize === 'FREE' ? 0 : cropped(cropSize),
  });

  useEffect(() => {
    const callFn = async () => {
      setImageLoader(true);
      getS3ImageAsBase64(imageUrl).then((base64String) => {
        setImage(base64String);
        setImageLoader(false);
      });
    };
    if (imageUrl) callFn();
  }, [imageUrl]);

  const [state, setState] = useState({
    size: editModeUrls?.size || 0,
    dimension: editModeUrls?.dimension || '',
    duration: editModeUrls?.duration || 0,
    fileName: editModeUrls?.fileName || '',
  });
  const [videoUrl, setVideo] = useState(null);
  const [thumbnail, setVideoThumbnail] = useState(null);

  useEffect(() => {
    if (imageUrl && isImageType) {
      setImage(imageUrl);
    }
    if (editModeUrls?.videoUrl && isVideoType) {
      setVideo(editModeUrls.videoUrl);
    }
  }, [imageUrl, editModeUrls, isImageType, isVideoType]);

  const errMsgsForExtenstions = (acceptedFiles) => {
    if (isEmpty(acceptedFiles)) {
      setValidatedSelectedFile({
        extensionError: isVideoType
          ? 'File format not valid. Please enter .mp4, .webm, .mov format files'
          : 'File format not valid. Please enter .jpeg, .jpg, .png format files',
      });
    } else {
      setValidatedSelectedFile({});
    }
  };

  const errMsgsForSize = (acceptedFiles) => {
    try {
      if (acceptedFiles.length > 0) {
        const fileSize = (acceptedFiles[0].size / 1024 / 1024).toFixed(0);
        if (fileSize > 50) {
          setValidatedSelectedFile({
            sizeError: 'Attachment size exceeds the allowable limit. Maximum upload file size: 50MB',
          });
          return false;
        }
        setValidatedSelectedFile({});
        if (acceptedFiles.length > 0) {
          const file = acceptedFiles[0];
          const fileSizes = (file.size / 1024 / 1024).toFixed(2); // size in MB
          const fileType = file.type;
          const reader = new FileReader();
          const fileName = file.name;
          reader.onload = () => {
            try {
              if (fileType.startsWith('image/')) {
                const img = new Image();
                img.src = reader.result;
                img.onload = () => {
                  const dimensions = `${img.width}x${img.height}`;
                  setImage(reader.result);
                  setState({
                    size: fileSizes,
                    dimension: dimensions,
                    fileType,
                    fileName,
                  });
                };
              } else if (fileType.startsWith('video/')) {
                setVideoThumbnailLoader(true);
                const videoUrl = URL.createObjectURL(file);
                const video = document.createElement('video');
                video.preload = 'metadata';
                video.src = videoUrl;
                video.onloadedmetadata = () => {
                  const { duration } = video;
                  extractThumbnail(video);
                  setVideo(reader.result);
                  setState({
                    size: fileSizes,
                    duration,
                    fileType,
                    fileName,
                  });
                };
              }
            } catch (error) {
              console.error('Error processing file: ', error);
              setValidatedSelectedFile({
                sizeError: 'An error occurred while processing the file.',
              });
            }
          };
          reader.readAsDataURL(file);
        }
      }
    } catch (error) {
      console.error('Error handling files: ', error);
      setValidatedSelectedFile({
        sizeError: 'An error occurred while handling the files.',
      });
    }
  };

  const onDrop = (acceptedFiles) => {
    errMsgsForExtenstions(acceptedFiles);
    errMsgsForSize(acceptedFiles);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: acceptFileTypes,
    multiple: false,
  });

  async function getS3ImageAsBase64(s3ImageUrl) {
    const response = await fetch(s3ImageUrl, {
      method: 'GET', // *GET, POST, PUT, DELETE, etc.
      mode: 'cors', // no-cors, *cors, same-origin
      cache: 'no-cache',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }

    // Convert the image to a base64 string
    const blob = await response.blob();
    return await convertBlobToBase64(blob);
  }

  function convertBlobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  const getCroppedImg = async (imageSrc, crop) => {
    return new Promise((resolve, reject) => {
      if (!crop || !previewCanvasRef.current) {
        reject(new Error('No crops or canvas available'));
        return;
      }
      const image = new Image();
      image.crossOrigin = 'anonymous'; // This is crucial for loading cross-origin images
      image.src = imageSrc;
      image.onload = () => {
        const canvas = previewCanvasRef.current;
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        canvas.width = crop.width;
        canvas.height = crop.height;
        const ctx = canvas.getContext('2d');
        const pixelRatio = window.devicePixelRatio;
        canvas.width = crop.width * pixelRatio;
        canvas.height = crop.height * pixelRatio;
        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(image, crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY, 0, 0, crop.width, crop.height);
        const base64String = canvas.toDataURL('image/png');
        resolve(base64String);
      };
      image.onerror = reject;
    });
  };

  const makeClientCrop = async (crops) => {
    if (image && crops.width >= 0 && crops.height >= 0) {
      const croppedImageUrl = await getCroppedImg(image, crops);
      setCroppedImage(croppedImageUrl);
    }
  };

  const handleCropComplete = (crops) => {
    makeClientCrop(crops);
    setCompletedCrop(crops);
  };

  const extractThumbnail = (video) => {
    video.currentTime = 5; // Setting the currentTime to a point where we can capture a valid frame
    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const thumbnailDataUrl = canvas.toDataURL('image/jpeg');
      setVideoThumbnail(thumbnailDataUrl);
      setTimeout(() => {
        setVideoThumbnailLoader(false);
      }, 2000);
    };
  };

  const onLoad = (img) => {
    imgRef.current = img;
  };

  const showErrMsgs = () => {
    return (
      Object.keys(validatedSelectedFile)?.length !== 0 && (
        <div className="d-flex justify-content-center align-items-center">
          <span className="text-danger">
            {validatedSelectedFile.sizeError}
            {validatedSelectedFile.extensionError}
          </span>
        </div>
      )
    );
  };

  const onUpload = () => {
    const cropppedImageClone = crop.height === 0 && crop.width === 0 ? image : croppedImage;
    onSubmit(image || editModeUrls?.imageUrl || videoUrl || editModeUrls?.videoUrl, cropppedImageClone, state, fileIndex, isVideoType, thumbnail);
  };
  return (
    <div>
      <div {...getRootProps()} className="dam-dropzone">
        <input {...getInputProps()} />
        <p className="mb-2">Drag and drop {title} here, or click the upload icon. Allowed file extensions:</p>
        <p className="mb-0">{acceptFileTypes}</p>
        <label className="upload-icon placement-right-bottom">
          <a href={() => false} className="align-items-center justify-content-center">
            <span className="icon-upload" />
          </a>
        </label>
      </div>
      {showErrMsgs()}
      <div className="upload-crop-section mt-3">
        <div className="crop-box">
          <p className="title">Uploaded {isVideoType ? 'video' : 'image & crop preview '} </p>
          <div className="image-view-box dam text-center">
            {(state?.fileType?.startsWith('image/') || editModeUrls?.imageUrl) &&
              (image || editModeUrls?.imageUrl) &&
              (!cropperImageLoader ? (
                <ReactCrop onImageLoaded={onLoad} src={image} crop={crop} onChange={(newCrop) => setCrop(newCrop)} onComplete={handleCropComplete} />
              ) : (
                <Loader />
              ))}
            {videoThumbnailloader ? (
              <Loader />
            ) : (
              (state?.fileType?.startsWith('video/') || editModeUrls?.videoUrl) && (
                <ReactPlayer
                  light={thumbnail || editModeUrls?.videoThumbnail}
                  className="react-player"
                  url={videoUrl || editModeUrls?.videoUrl}
                  width="100%"
                  height="100%"
                  controls
                />
              )
            )}
          </div>
        </div>
      </div>
      {isImageType && (
        <div style={{ display: 'none' }}>
          <canvas
            ref={previewCanvasRef}
            style={{
              border: '1px solid black',
              objectFit: 'contain',
              width: Math.round(completedCrop?.width ?? 0),
              height: Math.round(completedCrop?.height ?? 0),
            }}
          />
        </div>
      )}
      <div className="modal-buttons mt-4 d-flex justify-content-between">
        <button className="btn btn-default" type="button" onClick={() => onClose()}>
          {translate('DAM_MAIN_VIEW.ADD_EDIT_VIEW.BACK_BTN')}
        </button>
        <button onClick={() => onUpload()} type="button" className="btn btn-default">
          {translate('DAM_MAIN_VIEW.ADD_EDIT_VIEW.UPLOAD_IMAGE_BTN')}
        </button>
      </div>
    </div>
  );
}

export default DamImageDropzone;
